import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { logActivity } from "../lib/auditLog";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  analyzeReply,
  buildImportedLeads,
  buildMailtoUrl,
  buildOutreachDraft,
  buildReplyDraft,
  buildResearchLeads,
  summarizeOutreach,
} from "../lib/outreachAgent";
import {
  createOutreachMessage,
  createOutreachReplyEvent,
  loadLatestOutreachWorkspace,
  replaceOutreachLeads,
  saveOutreachCampaign,
  saveOutreachLead,
  saveOutreachLeads,
  sendApprovedOutreachEmail,
} from "../lib/outreachStorage";
import {
  BrainCircuit,
  CheckCircle2,
  Clipboard,
  ExternalLink,
  Mail,
  MessageSquareReply,
  Play,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
} from "lucide-react";

const storageKey = "spacem8_outreach_agent";

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Textarea({ className = "", ...props }) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-2xl border px-4 py-3 text-sm leading-6 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function Button({ children, className = "", variant = "default", ...props }) {
  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white shadow-sm hover:bg-orange-600";

  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-medium transition disabled:pointer-events-none disabled:opacity-50 ${styles} ${className}`}
    >
      {children}
    </button>
  );
}

function statusTone(status) {
  if (status === "Approved" || status === "Queued" || status === "Sent") return "bg-emerald-100 text-emerald-700";
  if (status?.includes("Reply")) return "bg-sky-100 text-sky-700";
  if (status?.includes("Closed")) return "bg-slate-200 text-slate-700";
  return "bg-amber-100 text-amber-800";
}

function scoreTone(score) {
  if (score >= 88) return "bg-emerald-100 text-emerald-700";
  if (score >= 78) return "bg-sky-100 text-sky-700";
  return "bg-amber-100 text-amber-800";
}

function StatCard({ label, value, icon: Icon, tone, isDark }) {
  return (
    <div className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>{label}</p>
          <h3 className={`mt-3 text-3xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{value}</h3>
        </div>
        <div className={`rounded-2xl p-3 ${tone}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
}

function LeadCard({ lead, selected, onSelect, isDark }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-2xl border p-4 text-left transition ${
        selected
          ? "border-sky-300 bg-sky-50 text-slate-900"
          : isDark
          ? "border-slate-800 bg-slate-950/50 text-white hover:bg-slate-800"
          : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words font-semibold">{lead.company}</p>
          <p className={`mt-1 text-xs ${selected ? "text-slate-500" : isDark ? "text-slate-400" : "text-slate-500"}`}>
            {lead.contactName} - {lead.role}
          </p>
        </div>
        <Badge className={scoreTone(lead.score)}>{lead.score}</Badge>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Badge className={statusTone(lead.status)}>{lead.status}</Badge>
        <Badge className="bg-slate-100 text-slate-700">{lead.scoreTier}</Badge>
      </div>
    </button>
  );
}

function loadSavedState() {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey));
    if (!saved) return null;
    return saved;
  } catch (_error) {
    return null;
  }
}

const defaultCampaign = {
  segment: "Property Managers",
  location: "Nairobi",
  offer: "a 15 minute SpaceM8 demo focused on rent follow-up, maintenance, and tenant communication",
  senderName: "SpaceM8 Team",
  senderEmail: "hello@spacem8.africa",
  schedulingLink: "https://cal.com/spacem8/demo",
};

export default function OutreachAgent({ isDark, setIsDark, currency, setCurrency }) {
  const toast = useToast();
  const { user } = useAuth();
  const saved = useMemo(loadSavedState, []);
  const [campaign, setCampaign] = useState(saved?.campaign || defaultCampaign);
  const [leads, setLeads] = useState(saved?.leads || []);
  const [campaignId, setCampaignId] = useState(saved?.campaignId || "");
  const [selectedId, setSelectedId] = useState(saved?.selectedId || "");
  const [query, setQuery] = useState("");
  const [replyText, setReplyText] = useState("");
  const [importText, setImportText] = useState("");
  const [isLoadingWorkspace, setIsLoadingWorkspace] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [storageError, setStorageError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadWorkspace = async () => {
      try {
        const workspace = await loadLatestOutreachWorkspace();
        if (!isMounted) return;

        if (workspace) {
          setCampaignId(workspace.campaignId);
          setCampaign(workspace.campaign || defaultCampaign);
          setLeads(workspace.leads || []);
          setSelectedId((current) => current || workspace.leads?.[0]?.id || "");
        }
        setStorageError("");
      } catch (error) {
        if (!isMounted) return;
        console.error("Outreach workspace load failed:", error);
        setStorageError("Supabase outreach tables are not ready yet. Apply supabase-outreach-agent.sql, then reload this page.");
      } finally {
        if (isMounted) setIsLoadingWorkspace(false);
      }
    };

    loadWorkspace();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        campaignId,
        campaign,
        leads,
        selectedId,
      })
    );
  }, [campaignId, campaign, leads, selectedId]);

  useEffect(() => {
    if (!campaignId || isLoadingWorkspace) return undefined;

    const timeoutId = setTimeout(async () => {
      try {
        await saveOutreachCampaign(campaignId, campaign, user?.id);
        setStorageError("");
      } catch (error) {
        console.error("Outreach campaign save failed:", error);
        setStorageError(`Could not save the outreach campaign to Supabase: ${error?.message || "Unknown error"}`);
      }
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [campaign, campaignId, isLoadingWorkspace, user?.id]);

  useEffect(() => {
    if (!selectedId && leads.length) {
      setSelectedId(leads[0].id);
    }
  }, [leads, selectedId]);

  const inputClass = isDark
    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
    : "border-slate-200 bg-white text-slate-900";

  const summary = useMemo(() => summarizeOutreach(leads), [leads]);
  const selectedLead = leads.find((lead) => lead.id === selectedId) || null;

  const filteredLeads = useMemo(() => {
    const normalizedQuery = query.toLowerCase();
    return leads.filter((lead) =>
      `${lead.company} ${lead.contactName} ${lead.segment} ${lead.location} ${lead.status}`
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [leads, query]);

  const updateCampaign = (key, value) => {
    setCampaign((current) => ({ ...current, [key]: value }));
  };

  const persistLead = useCallback(
    async (lead) => {
      if (!campaignId || !lead) return;

      try {
        await saveOutreachLead(campaignId, lead, user?.id);
        setStorageError("");
      } catch (error) {
        console.error("Outreach lead save failed:", error);
        setStorageError("Could not save the outreach lead to Supabase.");
      }
    },
    [campaignId, user?.id]
  );

  const updateLead = async (leadId, updater, options = {}) => {
    const { persist = true } = options;
    const currentLead = leads.find((lead) => lead.id === leadId);
    if (!currentLead) return;

    const nextLead = {
      ...(typeof updater === "function" ? updater(currentLead) : { ...currentLead, ...updater }),
      lastUpdated: new Date().toISOString(),
    };

    setLeads((current) =>
      current.map((lead) => (lead.id === leadId ? nextLead : lead))
    );

    if (persist) {
      await persistLead(nextLead);
    }
  };

  const handleRunResearch = async () => {
    setIsSaving(true);

    try {
      const nextCampaignId = await saveOutreachCampaign(campaignId, campaign, user?.id);
      setCampaignId(nextCampaignId);

      const nextLeads = buildResearchLeads({
        segment: campaign.segment,
        location: campaign.location,
        offer: campaign.offer,
        count: 5,
      }).map((lead) => ({
        ...lead,
        draft: buildOutreachDraft(lead, campaign),
      }));

      const savedLeads = await replaceOutreachLeads(nextCampaignId, nextLeads, user?.id);
      setLeads(savedLeads);
      setSelectedId(savedLeads[0]?.id || "");
      setStorageError("");

      await logActivity({
        action: "Ran outreach agent research",
        entityType: "outreach_agent",
        entityId: nextCampaignId,
        entityLabel: `${campaign.segment} in ${campaign.location}`,
        details: {
          campaign,
          leadCount: savedLeads.length,
          mode: "approval_required",
          storage: "supabase",
        },
      });

      toast.success("Research queue saved", "SpaceM8 prepared and saved lead scores with approval-ready email drafts.");
    } catch (error) {
      console.error("Outreach research run failed:", error);
      setStorageError("Could not save outreach research to Supabase. Confirm the outreach SQL has been applied.");
      toast.error("Research save failed", "Apply the outreach Supabase SQL, then try running the agent again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportLeads = async () => {
    const importedLeads = buildImportedLeads({
      rawText: importText,
      campaign,
      existingCount: leads.length,
    });

    if (!importedLeads.length) {
      toast.error("Import failed", "Add at least company and email for each lead.");
      return;
    }

    setIsSaving(true);

    try {
      const nextCampaignId = await saveOutreachCampaign(campaignId, campaign, user?.id);
      setCampaignId(nextCampaignId);

      const savedLeads = await saveOutreachLeads(nextCampaignId, importedLeads, user?.id);
      const nextLeads = [...leads, ...savedLeads];
      setLeads(nextLeads);
      setSelectedId(savedLeads[0]?.id || selectedId);
      setImportText("");
      setStorageError("");

      await logActivity({
        action: "Imported outreach leads",
        entityType: "outreach_agent",
        entityId: nextCampaignId,
        entityLabel: `${campaign.segment} in ${campaign.location}`,
        details: {
          importedLeadCount: savedLeads.length,
          mode: "approval_required",
          storage: "supabase",
        },
      });

      toast.success("Leads imported", `${savedLeads.length} lead${savedLeads.length === 1 ? "" : "s"} saved with draft emails.`);
    } catch (error) {
      console.error("Outreach lead import failed:", error);
      setStorageError("Could not import outreach leads to Supabase.");
      toast.error("Import failed", "SpaceM8 could not save the imported leads.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveDraft = async () => {
    if (!selectedLead) return;
    const nextLead = { ...selectedLead, status: "Approved", lastUpdated: new Date().toISOString() };
    await updateLead(selectedLead.id, nextLead);
    if (campaignId) {
      await createOutreachMessage({
        campaignId,
        lead: nextLead,
        userId: user?.id,
        direction: "outbound",
        status: "Approved",
        body: nextLead.draft,
      });
    }
    await logActivity({
      action: "Approved outreach email draft",
      entityType: "outreach_lead",
      entityId: selectedLead.id,
      entityLabel: selectedLead.company,
      details: {
        email: selectedLead.email,
        score: selectedLead.score,
      },
    });
    toast.success("Draft approved", "This lead is ready for sending from your email account.");
  };

  const handleOpenEmail = async () => {
    if (!selectedLead?.draft) return;
    window.location.href = buildMailtoUrl(selectedLead, selectedLead.draft);
    const nextLead = { ...selectedLead, status: "Queued", lastUpdated: new Date().toISOString() };
    await updateLead(selectedLead.id, nextLead);
    if (campaignId) {
      await createOutreachMessage({
        campaignId,
        lead: nextLead,
        userId: user?.id,
        direction: "outbound",
        status: "Queued",
        body: nextLead.draft,
      });
    }
    await logActivity({
      action: "Opened approved outreach email",
      entityType: "outreach_lead",
      entityId: selectedLead.id,
      entityLabel: selectedLead.company,
      details: {
        email: selectedLead.email,
        senderEmail: campaign.senderEmail,
      },
    });
  };

  const handleSendEmail = async () => {
    if (!campaignId || !selectedLead?.draft) return;
    if (selectedLead.status !== "Approved" && selectedLead.status !== "Queued") {
      toast.error("Approval required", "Approve the draft before sending it from SpaceM8.");
      return;
    }

    setIsSendingEmail(true);

    try {
      const result = await sendApprovedOutreachEmail({
        campaignId,
        leadId: selectedLead.id,
        draft: selectedLead.draft,
      });

      const nextLead = { ...selectedLead, status: "Sent", lastUpdated: new Date().toISOString() };
      setLeads((current) => current.map((lead) => (lead.id === selectedLead.id ? nextLead : lead)));
      setStorageError("");

      toast.success("Email sent", `Resend accepted the message${result?.id ? ` (${result.id})` : ""}.`);
    } catch (error) {
      console.error("Outreach email send failed:", error);
      setStorageError("Could not send the email. Confirm the Edge Function is deployed and RESEND_API_KEY is set.");
      toast.error("Send failed", "Check the outreach-send-email function and Resend settings.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied", `${label} copied to clipboard.`);
    } catch (_error) {
      toast.error("Copy failed", "Your browser blocked clipboard access.");
    }
  };

  const handleAnalyzeReply = async () => {
    if (!selectedLead || !replyText.trim()) return;

    const replyAnalysis = analyzeReply(replyText);
    const replyDraft = buildReplyDraft(selectedLead, replyAnalysis, campaign);

    const nextLead = {
      ...selectedLead,
      status: replyAnalysis.status,
      replyText,
      replyAnalysis,
      replyDraft,
      lastUpdated: new Date().toISOString(),
    };

    await updateLead(selectedLead.id, nextLead);
    if (campaignId) {
      await createOutreachReplyEvent({
        campaignId,
        lead: nextLead,
        userId: user?.id,
        replyText,
        analysis: replyAnalysis,
      });
    }

    await logActivity({
      action: "Analyzed outreach reply",
      entityType: "outreach_lead",
      entityId: selectedLead.id,
      entityLabel: selectedLead.company,
      details: {
        intent: replyAnalysis.intent,
        confidence: replyAnalysis.confidence,
        nextStep: replyAnalysis.nextStep,
      },
    });

    toast.success("Reply analyzed", `Intent: ${replyAnalysis.intent}`);
  };

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Growth Agent"
      title="Outreach Agent"
      description="Research target leads, score fit, draft approved emails, and analyze replies before responding."
      actions={
        <Button onClick={handleRunResearch} disabled={isSaving || isLoadingWorkspace} className="h-10 rounded-xl px-3 py-0">
          <Play className="mr-2 h-4 w-4" />
          {isSaving ? "Saving..." : "Run Agent"}
        </Button>
      }
    >
      {isLoadingWorkspace ? (
        <div className={`rounded-2xl border p-4 text-sm ${isDark ? "border-slate-800 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600"}`}>
          Loading saved outreach workspace...
        </div>
      ) : null}

      {storageError ? (
        <div className={`rounded-2xl border p-4 text-sm ${isDark ? "border-amber-800 bg-amber-950 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
          {storageError}
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        <StatCard label="Leads" value={summary.total} icon={Users} tone="bg-sky-100 text-sky-700" isDark={isDark} />
        <StatCard label="High Fit" value={summary.highFit} icon={Target} tone="bg-emerald-100 text-emerald-700" isDark={isDark} />
        <StatCard label="Drafts" value={summary.drafts} icon={Mail} tone="bg-amber-100 text-amber-800" isDark={isDark} />
        <StatCard label="Replies" value={summary.replies} icon={MessageSquareReply} tone="bg-orange-100 text-orange-700" isDark={isDark} />
      </section>

      <section className="grid gap-6 2xl:grid-cols-[0.95fr_1.05fr]">
        <div className={`rounded-[2rem] border p-5 shadow-sm ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="mb-5 flex items-start gap-3">
            <div className="rounded-2xl bg-sky-100 p-3 text-sky-700">
              <BrainCircuit className="h-5 w-5" />
            </div>
            <div>
              <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Campaign Brief</h2>
              <p className={`mt-1 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                The agent uses this brief to produce lead research, fit reasoning, and email drafts.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>Target segment</label>
              <Select value={campaign.segment} onChange={(event) => updateCampaign("segment", event.target.value)} className={inputClass}>
                <option>Property Managers</option>
                <option>Real Estate Developers</option>
                <option>Coworking Operators</option>
                <option>Serviced Apartments</option>
              </Select>
            </div>

            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>Market</label>
              <Input value={campaign.location} onChange={(event) => updateCampaign("location", event.target.value)} className={inputClass} />
            </div>

            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>Sender name</label>
              <Input value={campaign.senderName} onChange={(event) => updateCampaign("senderName", event.target.value)} className={inputClass} />
            </div>

            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>Sender email</label>
              <Input value={campaign.senderEmail} onChange={(event) => updateCampaign("senderEmail", event.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="mt-4 grid gap-4">
            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>Offer</label>
              <Textarea
                value={campaign.offer}
                onChange={(event) => updateCampaign("offer", event.target.value)}
                rows={3}
                className={inputClass}
              />
            </div>

            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>Scheduling link</label>
              <Input value={campaign.schedulingLink} onChange={(event) => updateCampaign("schedulingLink", event.target.value)} className={inputClass} />
            </div>
          </div>

          <div className={`mt-5 rounded-2xl border p-4 text-sm leading-6 ${isDark ? "border-slate-800 bg-slate-950 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
            <div className="mb-2 flex items-center gap-2 font-semibold">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Approval-first mode
            </div>
            SpaceM8 will prepare emails and reply drafts, but sending still requires review. Connect Gmail, Outlook, SendGrid, or Resend later for direct sending and inbox sync.
          </div>

          <div className={`mt-5 rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Import real leads</p>
                <p className={`mt-1 text-xs leading-5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Paste rows as company, contact name, role, email, website, notes.
                </p>
              </div>
              <Button
                onClick={handleImportLeads}
                disabled={isSaving || !importText.trim()}
                className="shrink-0 px-3 py-2"
              >
                Import
              </Button>
            </div>
            <Textarea
              value={importText}
              onChange={(event) => setImportText(event.target.value)}
              placeholder="Acme Properties, Jane Doe, Operations Manager, jane@acme.co.ke, https://acme.co.ke, Manages multi-unit rentals"
              rows={5}
              className={`${inputClass} font-mono text-xs`}
            />
          </div>
        </div>

        <div className={`rounded-[2rem] border p-5 shadow-sm ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Lead Research Queue</h2>
              <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Ranked leads with fit reasons and outreach status.</p>
            </div>
            <div className="relative w-full lg:w-72">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search leads..."
                className={`h-11 pl-11 ${inputClass}`}
              />
            </div>
          </div>

          <div className="grid max-h-[31rem] gap-3 overflow-y-auto pr-1">
            {filteredLeads.length ? (
              filteredLeads.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  selected={lead.id === selectedLead?.id}
                  onSelect={() => {
                    setSelectedId(lead.id);
                    setReplyText(lead.replyText || "");
                  }}
                  isDark={isDark}
                />
              ))
            ) : (
              <div className={`rounded-2xl border p-5 text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                Run the agent to create a research queue.
              </div>
            )}
          </div>
        </div>
      </section>

      <section className={`rounded-[2rem] border p-5 shadow-sm ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        {selectedLead ? (
          <div className="grid gap-6 2xl:grid-cols-[0.9fr_1.1fr]">
            <div>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={statusTone(selectedLead.status)}>{selectedLead.status}</Badge>
                    <Badge className={scoreTone(selectedLead.score)}>{selectedLead.score} fit score</Badge>
                  </div>
                  <h2 className={`mt-3 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{selectedLead.company}</h2>
                  <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                    {selectedLead.contactName}, {selectedLead.role} - {selectedLead.email}
                  </p>
                </div>
                <a
                  href={selectedLead.website}
                  target="_blank"
                  rel="noreferrer"
                  className={`inline-flex items-center rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                    isDark ? "border-slate-700 text-white hover:bg-slate-800" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Website
                </a>
              </div>

              <div className={`mt-5 rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Fit reason</p>
                <p className={`mt-2 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>{selectedLead.fitReason}</p>
                <div className="mt-4 grid gap-2">
                  {selectedLead.signals.map((signal) => (
                    <div key={signal} className="flex gap-2 text-sm">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span className={isDark ? "text-slate-300" : "text-slate-600"}>{signal}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <Button onClick={handleApproveDraft}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Approve Draft
                </Button>
                <Button
                  disabled={isSendingEmail || (selectedLead.status !== "Approved" && selectedLead.status !== "Queued")}
                  onClick={handleSendEmail}
                >
                  <Send className="mr-2 h-4 w-4" />
                  {isSendingEmail ? "Sending..." : "Send Email"}
                </Button>
                <Button
                  variant="outline"
                  disabled={selectedLead.status !== "Approved" && selectedLead.status !== "Queued"}
                  onClick={handleOpenEmail}
                  className={isDark ? "border-slate-700 text-white hover:bg-slate-800" : "border-slate-200 text-slate-700 hover:bg-slate-50"}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Open Email
                </Button>
              </div>
            </div>

            <div className="grid gap-5">
              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Email Draft</h3>
                  <Button
                    variant="outline"
                    onClick={() => handleCopy(selectedLead.draft, "Email draft")}
                    className={`px-3 py-2 ${isDark ? "border-slate-700 text-white hover:bg-slate-800" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                  >
                    <Clipboard className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea
                  value={selectedLead.draft}
                  onChange={(event) => updateLead(selectedLead.id, { draft: event.target.value, status: "Research" }, { persist: false })}
                  onBlur={() => persistLead(selectedLead)}
                  rows={10}
                  className={`${inputClass} font-mono text-xs`}
                />
              </div>

              <div className={`rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-sky-600" />
                  <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Reply Analyzer</h3>
                </div>
                <Textarea
                  value={replyText}
                  onChange={(event) => setReplyText(event.target.value)}
                  placeholder="Paste a reply from the contacted lead..."
                  rows={5}
                  className={inputClass}
                />
                <div className="mt-3 flex flex-wrap gap-3">
                  <Button onClick={handleAnalyzeReply} disabled={!replyText.trim()}>
                    <MessageSquareReply className="mr-2 h-4 w-4" />
                    Analyze Reply
                  </Button>
                  {selectedLead.replyDraft ? (
                    <Button
                      variant="outline"
                      onClick={() => handleCopy(selectedLead.replyDraft, "Reply draft")}
                      className={isDark ? "border-slate-700 text-white hover:bg-slate-800" : "border-slate-200 text-slate-700 hover:bg-slate-50"}
                    >
                      <Clipboard className="mr-2 h-4 w-4" />
                      Copy Reply
                    </Button>
                  ) : null}
                </div>

                {selectedLead.replyAnalysis ? (
                  <div className="mt-4 grid gap-3 md:grid-cols-[0.7fr_1.3fr]">
                    <div className={`rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                      <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>Intent</p>
                      <p className={`mt-2 text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{selectedLead.replyAnalysis.intent}</p>
                      <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>{selectedLead.replyAnalysis.confidence}% confidence</p>
                    </div>
                    <div className={`rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                      <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>Next step</p>
                      <p className={`mt-2 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>{selectedLead.replyAnalysis.nextStep}</p>
                    </div>
                    <div className="md:col-span-2">
                      <Textarea
                        value={selectedLead.replyDraft}
                        onChange={(event) => updateLead(selectedLead.id, { replyDraft: event.target.value }, { persist: false })}
                        onBlur={() => persistLead(selectedLead)}
                        rows={7}
                        className={`${inputClass} font-mono text-xs`}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div className={`rounded-2xl border p-5 text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
            Run the agent to select a lead and review the first draft.
          </div>
        )}
      </section>
    </AppLayout>
  );
}
